import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';

// Rescisão → "Assinatura": e-mail ao colaborador com os documentos da rescisão.
// O modelo (pedido do empregado × dispensa) vem do tipo do desligamento na Convenia;
// sem o tipo, a operadora escolhe. Os PDFs são os do Financeiro (mesmo cadastro e mesma
// conferência de pessoa e empresa). Este modal é a prévia: de/para/cc, assunto, texto
// editável e os anexos que vão junto; o Enviar fica aqui dentro e exige ao menos um PDF.
const API = process.env.REACT_APP_API_BASE;
const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('access')}` });
const kb = (n) => (n ? `${Math.max(1, Math.round(n / 1024))} KB` : '');

const Selo = ({ rotulo, valor, semNome }) => {
  const cfg = valor === true ? { cor: '#116b47', bg: '#e6f7ef', txt: 'confere' }
    : valor === false ? { cor: '#a11d2b', bg: '#fdeaec', txt: 'DIVERGE' }
      : semNome ? { cor: '#6c757d', bg: '#f1f3f5', txt: 'sem nome no documento' }
        : { cor: '#6c757d', bg: '#f1f3f5', txt: 'inconclusivo' };
  return (
    <span className="badge me-1" style={{ background: cfg.bg, color: cfg.cor, border: `1px solid ${cfg.cor}33`, fontWeight: 600 }}>
      {rotulo}: {cfg.txt}
    </span>
  );
};

const EmailRescisaoColaborador = ({ clienteId, conveniaId, colaborador, onFechar, onEnviado }) => {
  const [d, setD] = useState(null);
  const [form, setForm] = useState(null);       // {operador_id, para, cc, assunto}
  const [modelo, setModelo] = useState(null);
  const [marcados, setMarcados] = useState([]);  // ids dos anexos que vão no e-mail
  const [confirmou, setConfirmou] = useState(false);
  const [carregando, setCarregando] = useState(true);
  const [enviando, setEnviando] = useState(false);
  const [anexando, setAnexando] = useState(false);
  const [erro, setErro] = useState('');
  const [versao, setVersao] = useState(0);
  const [corpo, setCorpo] = useState('');
  const arquivoRef = useRef(null);
  const primeiraRef = useRef(true);

  const carregar = useCallback((extra, manterTexto = false) => {
    setCarregando(true);
    setErro('');
    axios.get(`${API}/api/bporh/rescisao/email-colaborador/`,
      { params: { cliente_id: clienteId, convenia_id: conveniaId, ...(extra || {}) }, headers: headers() })
      .then((r) => {
        const x = r.data;
        setD(x);
        setModelo(x.modelo);
        setForm((f) => ({
          operador_id: f?.operador_id || x.operador_sugerido_id || '',
          para: f?.para ?? (x.para || ''),
          cc: f?.cc ?? '',
          assunto: (manterTexto && f?.assunto) || x.assunto || '',
        }));
        // 1ª abertura: todo anexo válido entra marcado; depois vale a escolha da operadora
        // (o recém-anexado é marcado em anexar), e o bloqueado nunca fica marcado
        const validos = (x.anexos || []).filter((a) => !(a.laudo || {}).bloqueia).map((a) => a.id);
        setMarcados((m) => (primeiraRef.current ? validos : m.filter((id) => validos.includes(id))));
        primeiraRef.current = false;
        if (!manterTexto) { setCorpo(x.corpo_html || ''); setVersao((n) => n + 1); }
      })
      .catch((e) => setErro(e.response?.data?.mensagem || 'Não foi possível montar a prévia'))
      .finally(() => setCarregando(false));
  }, [clienteId, conveniaId]);
  useEffect(() => { carregar(); }, [carregar]);

  const trocarModelo = (m) => { if (m !== modelo) carregar({ modelo: m, operador_id: form?.operador_id }); };

  const anexar = (arq) => {
    if (!arq) return;
    const fd = new FormData();
    fd.append('arquivo', arq); fd.append('tipo', 'rescisao'); fd.append('convenia_id', conveniaId);
    setAnexando(true);
    setErro('');
    axios.post(`${API}/api/bporh/financeiro/${clienteId}/anexar/`, fd, { headers: headers() })
      .then((r) => {
        const a = r.data.anexo;
        if (a && !(a.laudo || {}).bloqueia) setMarcados((m) => [...m, a.id]);
        carregar({ modelo, operador_id: form?.operador_id }, true);
        if ((a?.laudo || {}).bloqueia) setErro(r.data.mensagem);
      })
      .catch((e) => setErro(e.response?.data?.mensagem || 'Não foi possível anexar'))
      .finally(() => { setAnexando(false); if (arquivoRef.current) arquivoRef.current.value = ''; });
  };

  const verPdf = (id) => {
    axios.get(`${API}/api/bporh/financeiro/${clienteId}/anexo/${id}/`, { headers: headers(), responseType: 'blob' })
      .then((r) => window.open(URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' })), '_blank'))
      .catch(() => setErro('Não foi possível abrir o PDF'));
  };

  const anexosMarcados = (d?.anexos || []).filter((a) => marcados.includes(a.id));
  const precisaConfirmar = anexosMarcados.some((a) => !a.laudo || (a.laudo.ressalvas || []).length > 0);

  const enviar = () => {
    setEnviando(true);
    setErro('');
    axios.post(`${API}/api/bporh/rescisao/email-colaborador/`, {
      acao: 'enviar', cliente_id: clienteId, convenia_id: conveniaId, modelo, operador_id: form.operador_id,
      destinatarios: form.para, cc: form.cc, assunto: form.assunto, corpo_html: corpo,
      anexo_ids: marcados, confirmar_ressalvas: confirmou,
    }, { headers: headers() })
      .then((r) => onEnviado && onEnviado(r.data.mensagem))
      .catch((e) => setErro(e.response?.data?.mensagem || 'Não foi possível enviar'))
      .finally(() => setEnviando(false));
  };

  const op = (d?.operadores || []).find((o) => String(o.id) === String(form?.operador_id));
  const podeEnviar = modelo && form?.operador_id && form?.para?.trim() && form?.assunto?.trim()
    && anexosMarcados.length > 0 && (!precisaConfirmar || confirmou);
  const motivoTrava = !modelo ? 'Escolha o modelo do e-mail'
    : anexosMarcados.length === 0 ? 'Marque ou anexe ao menos um documento da rescisão'
      : precisaConfirmar && !confirmou ? 'Confirme a conferência dos anexos com ressalva' : '';

  return (
    <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 10002 }}
      onClick={(e) => { if (e.target === e.currentTarget) onFechar(); }}>
      <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
        <div className="modal-content shadow-lg">
          <div className="modal-header bg-white">
            <div>
              <h5 className="modal-title" style={{ color: '#212529' }}>
                <i className="fas fa-file-signature me-2" style={{ color: '#00b5cc' }}></i>E-mail ao colaborador · documentos da rescisão
              </h5>
              <div className="small" style={{ color: '#6c757d' }}>
                {colaborador}{d?.cliente ? ` · ${d.cliente}` : ''}{d?.data_desligamento ? ` · desligamento ${d.data_desligamento}` : ''}
              </div>
            </div>
            <button type="button" className="btn-close" onClick={onFechar}></button>
          </div>

          <div className="modal-body">
            {carregando && !d && (
              <div className="text-muted small"><span className="spinner-border spinner-border-sm me-2"></span>Conferindo o tipo do desligamento na Convenia…</div>
            )}
            {erro && <div className="alert alert-danger py-2 small">{erro}</div>}

            {d && (
              <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
                <span className="small fw-semibold">Modelo:</span>
                <div className="btn-group btn-group-sm" role="group" aria-label="Modelo do e-mail">
                  {(d.modelos || []).map((m) => (
                    <button key={m.id} type="button" disabled={carregando}
                      className={`btn ${modelo === m.id ? 'btn-primary' : 'btn-outline-primary'}`}
                      onClick={() => trocarModelo(m.id)}>{m.rotulo}</button>
                  ))}
                </div>
                <span className="small text-muted">
                  {d.tipo_desligamento
                    ? <>Convenia: <b>{d.tipo_desligamento}</b>{d.modelo_sugerido && modelo !== d.modelo_sugerido ? ' — o modelo escolhido é diferente do tipo na Convenia' : ''}</>
                    : <>Tipo do desligamento não encontrado{d.erro_tipo ? ` (${d.erro_tipo})` : ''}: escolha o modelo.</>}
                </span>
              </div>
            )}
            {d?.ultimo_envio && (
              <div className="alert alert-info py-2 small">
                <i className="fas fa-clock-rotate-left me-2"></i>
                Já enviado em <b>{d.ultimo_envio.em}</b>{d.ultimo_envio.operador ? ` por ${d.ultimo_envio.operador}` : ''}
                {d.ultimo_envio.destinatarios ? ` para ${d.ultimo_envio.destinatarios}` : ''} ({d.ultimo_envio.modelo})
                {(d.ultimo_envio.anexos || []).length > 0 ? ` com ${d.ultimo_envio.anexos.join(', ')}` : ''}. Enviar de novo manda outro e-mail.
              </div>
            )}

            {d && form && (
              <>
                <div className="row g-2 mb-3">
                  <div className="col-md-6">
                    <label className="form-label small fw-semibold mb-1" htmlFor="resc-email-op">De (operadora que assina)</label>
                    <select id="resc-email-op" className="form-select form-select-sm" value={form.operador_id}
                      onChange={(e) => { setForm({ ...form, operador_id: e.target.value }); }}
                      onBlur={() => modelo && carregar({ modelo, operador_id: form.operador_id })}>
                      <option value="">Selecione…</option>
                      {(d.operadores || []).map((o) => (
                        <option key={o.id} value={o.id} disabled={!o.pronto}>
                          {o.nome}{o.email ? ` · ${o.email}` : ''}{o.pronto ? '' : ' (sem senha de app)'}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label small fw-semibold mb-1" htmlFor="resc-email-para">Para (e-mail pessoal)</label>
                    <input id="resc-email-para" type="text" className="form-control form-control-sm" value={form.para}
                      onChange={(e) => setForm({ ...form, para: e.target.value })} />
                    {!d.para && <div className="form-text text-danger">Sem e-mail pessoal na Convenia — informe aqui.</div>}
                  </div>
                  <div className="col-md-6">
                    <label className="form-label small fw-semibold mb-1" htmlFor="resc-email-cc">Cc (opcional)</label>
                    <input id="resc-email-cc" type="text" className="form-control form-control-sm" value={form.cc} placeholder="separe por ;"
                      onChange={(e) => setForm({ ...form, cc: e.target.value })} />
                    {(d.grupo_cliente || []).length > 0 && (
                      <div className="form-text">Também vai {d.grupo_modo === 'bcc' ? 'em cópia oculta' : 'em cópia'} para o grupo do cliente: {d.grupo_cliente.join(', ')}</div>
                    )}
                  </div>
                  <div className="col-md-6">
                    <label className="form-label small fw-semibold mb-1" htmlFor="resc-email-assunto">Assunto</label>
                    <input id="resc-email-assunto" type="text" className="form-control form-control-sm" value={form.assunto}
                      onChange={(e) => setForm({ ...form, assunto: e.target.value })} />
                  </div>
                </div>

                {/* ── anexos: o que vai junto no e-mail ── */}
                <div className="border rounded p-2 mb-3" style={{ background: '#f8f9fb' }}>
                  <div className="d-flex align-items-center justify-content-between mb-2">
                    <span className="small fw-semibold"><i className="fas fa-paperclip me-1"></i>
                      Anexos que vão no e-mail: {anexosMarcados.length}</span>
                    <div>
                      <input ref={arquivoRef} type="file" hidden accept=".pdf" onChange={(e) => anexar(e.target.files[0])} />
                      <button type="button" className="btn btn-sm btn-outline-primary" disabled={anexando}
                        onClick={() => arquivoRef.current?.click()}>
                        {anexando ? <><span className="spinner-border spinner-border-sm me-1"></span>Conferindo…</>
                          : <><i className="fas fa-file-arrow-up me-1"></i>Anexar PDF</>}
                      </button>
                    </div>
                  </div>
                  {(d.anexos || []).length === 0 && (
                    <div className="small text-danger"><i className="fas fa-triangle-exclamation me-1"></i>
                      Nenhum documento da rescisão anexado. Anexe o TRCT, o demonstrativo e as guias antes de enviar.</div>
                  )}
                  {(d.anexos || []).map((a) => {
                    const l = a.laudo || {};
                    return (
                      <div key={a.id} className="d-flex align-items-start gap-2 py-1 border-top">
                        <input type="checkbox" className="form-check-input mt-1" id={`resc-anexo-${a.id}`}
                          checked={marcados.includes(a.id)} disabled={!!l.bloqueia}
                          onChange={(e) => setMarcados((m) => (e.target.checked ? [...m, a.id] : m.filter((x) => x !== a.id)))} />
                        <div className="flex-grow-1" style={{ minWidth: 0 }}>
                          <label htmlFor={`resc-anexo-${a.id}`} className="small fw-semibold mb-0">
                            <i className="fas fa-file-pdf text-danger me-1"></i>{a.nome_original}
                          </label>
                          <span className="small text-muted ms-2">{kb(a.tamanho)}{a.criado_em ? ` · ${a.criado_em}` : ''}</span>
                          <button type="button" className="btn btn-link btn-sm p-0 ms-2" onClick={() => verPdf(a.id)}>abrir</button>
                          <div className="mt-1">
                            <Selo rotulo="Pessoa" valor={l.pessoa} semNome={!!l.pessoa_sem_nome} />
                            <Selo rotulo="Empresa" valor={l.empresa} />
                          </div>
                          {l.bloqueia && <div className="small fw-semibold" style={{ color: '#a11d2b' }}>
                            <i className="fas fa-ban me-1"></i>Bloqueado: {l.motivo_bloqueio}</div>}
                          {!l.bloqueia && (l.ressalvas || []).length > 0 && <div className="small" style={{ color: '#8a6d00' }}>
                            <i className="fas fa-triangle-exclamation me-1"></i>{l.ressalvas.join('; ')}</div>}
                        </div>
                      </div>
                    );
                  })}
                  {precisaConfirmar && (
                    <div className="form-check mt-2">
                      <input className="form-check-input" type="checkbox" id="resc-email-confirma" checked={confirmou}
                        onChange={(e) => setConfirmou(e.target.checked)} />
                      <label className="form-check-label small" htmlFor="resc-email-confirma">
                        Abri e conferi os anexos com ressalva: são deste colaborador e desta rescisão.
                      </label>
                    </div>
                  )}
                </div>

                {modelo ? (
                  <>
                    <label className="form-label small fw-semibold mb-1">Texto do e-mail (clique para editar)</label>
                    <div key={versao} className="border rounded p-3 bg-white" style={{ minHeight: 240, outline: 'none' }}
                      contentEditable suppressContentEditableWarning
                      ref={(el) => { if (el && !el.dataset.pronto) { el.innerHTML = corpo; el.dataset.pronto = '1'; } }}
                      onInput={(e) => setCorpo(e.currentTarget.innerHTML)} />
                  </>
                ) : (
                  <div className="alert alert-warning py-2 small mb-0">Escolha o modelo do e-mail acima para ver o texto.</div>
                )}
              </>
            )}
          </div>

          <div className="modal-footer bg-white">
            {motivoTrava && <span className="small text-muted me-auto">{motivoTrava}</span>}
            <button type="button" className="btn btn-secondary" onClick={onFechar}>Fechar</button>
            <button type="button" className="btn btn-primary" disabled={enviando || carregando || !podeEnviar}
              onClick={enviar}>
              {enviando
                ? <><span className="spinner-border spinner-border-sm me-2"></span>Enviando…</>
                : <><i className="fas fa-paper-plane me-2"></i>Enviar com {anexosMarcados.length} anexo(s){op ? ` como ${op.nome}` : ''}</>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailRescisaoColaborador;
